<!-- Player of a voice note: play/pause, the waveform of the recording (bars in the main colour up to the point
     played; a tap or a drag on it moves there; arrows on the keyboard move 5 seconds) and the time. The waveform comes
     from the decoded audio (src/features/waveform.ts); when it cannot be decoded the bars stay flat and the player still
     works. Used by "Drop your idea here" (IdeaDrop.vue) and by the video dialog. pause() is exposed. -->
<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { WAVE_BARS, WAVE_MIN, audioContextClass, barsFor, peaksOf, resample, waveClock } from '../features/waveform'
import { useI18n } from '../composables/useI18n'

const props = defineProps<{ src: string }>()
const { t } = useI18n()

const audio = ref<HTMLAudioElement | null>(null)
const flat = () => Array(WAVE_BARS).fill(WAVE_MIN) as number[]
/** The fine waveform of the note; the bars shown are made from it for the width of the box */
const fine = ref<number[]>(flat())
const wave = ref<HTMLElement | null>(null)
const nBars = ref(40)
const peaks = computed(() => resample(fine.value, nBars.value))
let resize: ResizeObserver | null = null
onMounted(() => {
  if (!wave.value) return
  nBars.value = barsFor(wave.value.clientWidth)
  if (typeof ResizeObserver !== 'undefined') {
    resize = new ResizeObserver(() => { if (wave.value) nBars.value = barsFor(wave.value.clientWidth) })
    resize.observe(wave.value)
  }
})
const playing = ref(false)
const cur = ref(0)
const dur = ref(0)

// Waveform and length from the decoded audio (a recorded webm often has no length in the <audio> element)
let seq = 0
async function decode(src: string) {
  const my = ++seq
  fine.value = flat()
  dur.value = 0
  cur.value = 0
  playing.value = false
  const AC = audioContextClass()
  if (!src || src.startsWith('media:') || !AC) return   // a stored note not loaded yet: its address comes later
  try {
    const buf = await (await fetch(src)).arrayBuffer()
    const ctx = new AC()
    try {
      const a = await ctx.decodeAudioData(buf)
      if (my !== seq) return
      fine.value = peaksOf(a.getChannelData(0))
      dur.value = a.duration
    } finally {
      void ctx.close()
    }
  } catch {
    /* not decodable here: flat bars */
  }
}
watch(() => props.src, decode, { immediate: true })

// Smooth progress while playing
let raf = 0
function tick() {
  if (audio.value) cur.value = audio.value.currentTime
  if (playing.value) raf = requestAnimationFrame(tick)
}
function onPlay() { playing.value = true; cancelAnimationFrame(raf); raf = requestAnimationFrame(tick) }
function onPause() { playing.value = false; cancelAnimationFrame(raf); if (audio.value) cur.value = audio.value.currentTime }
function onEnded() { onPause(); cur.value = 0 }
function onMeta() { const d = audio.value?.duration; if (d && Number.isFinite(d)) dur.value = d }

function toggle() {
  const a = audio.value
  if (!a) return
  if (a.paused) void a.play().catch(() => { playing.value = false })
  else a.pause()
}
function pause() { audio.value?.pause() }
function seek(sec: number) {
  const a = audio.value
  if (!a || !dur.value) return
  a.currentTime = Math.max(0, Math.min(dur.value, sec))
  cur.value = a.currentTime
}

// Tap or drag on the waveform
let dragging = false
function seekAt(e: PointerEvent) {
  const r = (e.currentTarget as HTMLElement).getBoundingClientRect()
  if (r.width) seek((e.clientX - r.left) / r.width * dur.value)
}
function down(e: PointerEvent) {
  if (!dur.value) return
  dragging = true;
  (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId)
  seekAt(e)
}
function move(e: PointerEvent) { if (dragging) seekAt(e) }
function up() { dragging = false }
function key(e: KeyboardEvent) {
  if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
    e.preventDefault()
    seek(cur.value + (e.key === 'ArrowRight' ? 5 : -5))
  }
}

const progress = computed(() => (dur.value ? cur.value / dur.value : 0))
const time = computed(() => waveClock(playing.value || cur.value > 0 ? cur.value : dur.value))

onBeforeUnmount(() => { cancelAnimationFrame(raf); resize?.disconnect(); pause() })
defineExpose({ pause })
</script>

<template>
  <div class="flex items-center gap-3 flex-1 min-w-0">
    <audio ref="audio" :src="src" preload="metadata" class="hidden" @play="onPlay" @pause="onPause" @ended="onEnded" @loadedmetadata="onMeta"></audio>
    <button type="button" :title="playing ? t('Pause') : t('Play')" :aria-label="playing ? t('Pause') : t('Play')"
      class="w-10 h-10 rounded-full bg-brand hover:bg-brandDark text-white flex items-center justify-center flex-shrink-0 transition active:scale-95" @click="toggle">
      <i :class="['fas', playing ? 'fa-pause' : 'fa-play pl-0.5', 'text-sm']"></i>
    </button>
    <div ref="wave" role="slider" tabindex="0" :aria-label="t('Position in the voice note')" aria-valuemin="0" :aria-valuemax="Math.round(dur)" :aria-valuenow="Math.round(cur)" :aria-valuetext="time"
      class="flex-1 min-w-0 h-9 flex items-center justify-between overflow-hidden cursor-pointer touch-none focus:outline-none" @pointerdown="down" @pointermove="move" @pointerup="up" @pointercancel="up" @keydown="key">
      <span v-for="(h, i) in peaks" :key="i" :class="['w-[3px] flex-shrink-0 rounded-full transition-colors', (i + 0.5) / peaks.length <= progress ? 'bg-brand' : 'bg-gray-600']" :style="{ height: h * 100 + '%' }"></span>
    </div>
    <span class="text-[11px] font-semibold text-gray-400 tabular-nums w-9 text-right flex-shrink-0">{{ time }}</span>
  </div>
</template>
